import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { getCognitoUserPool, isLocalDev, getApiUrl } from './cognitoPool';
import { logger } from './logger';

// In-flight GET deduplication
//
// Symptom (2026-05-19, test env): the HiFi edit-page fan-out + concurrent
// component mounts (EditObject.tsx, useArtworkDetailsForm, ProvenanceTab,
// etc.) all independently fetch the same endpoints for the same catalog
// item. CloudWatch logs show 7× concurrent ``GET /api/additional-info/{id}``
// for the same UUID inside a single user session, plus 2× concurrent
// ``GET /api/keywords/{id}``. Each duplicate request opens its own PG
// session on the backend (no request-coalescing there either) and
// contributes to event-loop saturation on the worker.
//
// This Map coalesces idempotent GETs by ``method + url`` while at least one
// caller's promise is still pending. Callers that arrive while a request is
// in flight receive the *same* promise — so all callers see the response
// at the same time, with one network request, one auth round-trip, and one
// PG round-trip. Once the promise settles (resolve OR reject) the entry is
// cleared so the next call hits the network fresh.
//
// SCOPE — applies ONLY to GETs. POST/PUT/PATCH/DELETE are NOT deduped:
//  • POSTs may carry different payloads to the same URL
//  • PUTs/PATCHes may be intentionally retried with new state
//  • DELETEs are idempotent at the protocol level but rare enough that
//    dedup-on-collision is not a meaningful win
// If callers want POST dedup they should serialise at the call site (the
// autosave hook already does this via ``lastSerializedRef``).
//
// Query-string differences are part of the key (the full URL string is
// used), so ``?include=desc`` and ``?include=desc,kw`` dedupe separately.
const pendingGets = new Map<string, Promise<AxiosResponse>>();

// Tokens returned together because both come from the same Cognito session —
// the ID token (Authorization: Bearer) carries user claims for backend auth,
// while the access token (X-Cognito-Access-Token) is required by Cognito's
// user-scoped APIs (GetUserAttributeVerificationCode, VerifyUserAttribute).
interface ValidTokens {
  idToken: string;
  accessToken: string;
}

// Helper function to get valid tokens with refresh capability
const getValidTokens = (cognitoUser: { getSession: (callback: (err: Error | null, session: CognitoUserSession | null) => void) => void; refreshSession: (refreshToken: unknown, callback: (err: Error | null, session: CognitoUserSession | null) => void) => void }): Promise<ValidTokens | null> => {
  const extract = (session: CognitoUserSession | null): ValidTokens | null => {
    const id = session?.getIdToken().getJwtToken();
    const access = session?.getAccessToken().getJwtToken();
    return id && access ? { idToken: id, accessToken: access } : null;
  };

  return new Promise((resolve) => {
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        resolve(null);
        return;
      }

      // Check if token expires in less than 5 minutes - refresh proactively
      const idToken = session?.getIdToken();
      if (idToken) {
        const payload = idToken.decodePayload();
        const expiresIn = payload.exp - Math.floor(Date.now() / 1000);

        if (expiresIn < 300) {
          cognitoUser.refreshSession(session.getRefreshToken(), (refreshErr: Error | null, newSession: CognitoUserSession | null) => {
            if (refreshErr || !newSession?.isValid()) {
              // Return existing tokens if refresh fails but current session still valid
              if (expiresIn > 0) {
                resolve(extract(session));
              } else {
                resolve(null);
              }
              return;
            }
            resolve(extract(newSession));
          });
          return;
        }
      }

      if (!session?.isValid()) {
        cognitoUser.refreshSession(session.getRefreshToken(), (refreshErr: Error | null, newSession: CognitoUserSession | null) => {
          if (refreshErr || !newSession?.isValid()) {
            resolve(null);
            return;
          }
          resolve(extract(newSession));
        });
        return;
      }

      resolve(extract(session));
    });
  });
};

let apiInstance: AxiosInstance | null = null;

// Lazy-initialized singleton axios instance with auth interceptors
const getApiInstance = (): AxiosInstance => {
  if (apiInstance) {
    return apiInstance;
  }

  const apiUrl = getApiUrl();
  if (!apiUrl) {
    throw new Error('API URL not configured');
  }

  // 30s timeout converts a stuck/hung backend into a clean error instead of a
  // request that hangs forever in the browser. Without this, autosave-style
  // flows can sit in "pending" indefinitely, masking real backend issues and
  // (in the worst case) leaving load → save handshakes in inconsistent states.
  // 30s is generous enough to cover the slowest legitimate endpoints (multipart
  // upload completion, large search reindex paths) without making real hangs
  // invisible.
  const instance = axios.create({ baseURL: apiUrl, timeout: 30000 });

  // Add request interceptor to include Cognito tokens.
  //
  // Two headers are set:
  //   - Authorization: Bearer <id-token>         — for backend auth (get_current_user)
  //   - X-Cognito-Access-Token: <access-token>   — for endpoints that call
  //     Cognito user-scoped APIs (SMS MFA start/verify).
  //
  // Setting the access token header unconditionally is safe: endpoints that
  // don't need it simply ignore it. Keeping both tokens colocated avoids a
  // second Cognito session read per SMS MFA request.
  instance.interceptors.request.use(
    async (config) => {
      // For local development, use stored local dev tokens
      if (isLocalDev()) {
        const localIdToken = localStorage.getItem('localDevIdToken');
        const localAccessToken = localStorage.getItem('localDevAccessToken');
        if (localIdToken) {
          config.headers.Authorization = `Bearer ${localIdToken}`;
        }
        if (localAccessToken) {
          config.headers['X-Cognito-Access-Token'] = localAccessToken;
        }
        return config;
      }

      // For production, use Cognito SDK via shared singleton pool
      try {
        const userPool = getCognitoUserPool();
        const cognitoUser = userPool.getCurrentUser();

        if (cognitoUser) {
          const tokens = await getValidTokens(cognitoUser);
          if (tokens) {
            config.headers.Authorization = `Bearer ${tokens.idToken}`;
            config.headers['X-Cognito-Access-Token'] = tokens.accessToken;
          }
        }
      } catch (error) {
        logger.warn('Failed to get Cognito token for API request', error);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle auth errors
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        // For local development, check if we have a local token
        if (isLocalDev()) {
          const localToken = localStorage.getItem('localDevIdToken');
          if (!localToken) {
            window.location.href = '/auth';
          }
          return Promise.reject(error);
        }

        // For production, use Cognito SDK via shared singleton pool
        try {
          const userPool = getCognitoUserPool();
          const cognitoUser = userPool.getCurrentUser();
          if (cognitoUser) {
            cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
              if (err || !session?.isValid()) {
                cognitoUser.signOut();
                window.location.href = '/auth';
              }
            });
          } else {
            window.location.href = '/auth';
          }
        } catch (error) {
          logger.warn('Failed to handle 401 error in response interceptor', error);
        }
      }
      return Promise.reject(error);
    }
  );

  apiInstance = instance;
  return instance;
};

export const _resetApiForTesting = (): void => {
  apiInstance = null;
  pendingGets.clear();
};

// Dedupe key: full URL string. Query-string differences produce distinct
// keys naturally because axios's ``url`` reflects the resolved request URL
// at the call site. If a caller passes a custom ``params`` object via
// config, we include its JSON serialisation so ``GET /foo?x=1`` and
// ``GET /foo`` collide correctly.
const dedupeKey = (url: string, config?: Record<string, unknown>): string => {
  const params = config?.params;
  if (params && typeof params === 'object') {
    try {
      return `${url}|${JSON.stringify(params)}`;
    } catch {
      // Unserialisable params — fall through and key by URL alone.
    }
  }
  return url;
};

const dedupedGet = (url: string, config?: Record<string, unknown>): Promise<AxiosResponse> => {
  const key = dedupeKey(url, config);
  const existing = pendingGets.get(key);
  if (existing) {
    return existing;
  }
  const pending = getApiInstance()
    .get(url, config)
    .finally(() => {
      // Clear only if we still own the slot — defends against a future
      // re-entrant call clearing a fresher request's entry.
      if (pendingGets.get(key) === pending) {
        pendingGets.delete(key);
      }
    });
  pendingGets.set(key, pending);
  return pending;
};

const api = {
  get: dedupedGet,
  post: (url: string, data?: Record<string, unknown>, config?: Record<string, unknown>) => getApiInstance().post(url, data, config),
  put: (url: string, data?: Record<string, unknown>, config?: Record<string, unknown>) => getApiInstance().put(url, data, config),
  patch: (url: string, data?: Record<string, unknown>, config?: Record<string, unknown>) => getApiInstance().patch(url, data, config),
  delete: (url: string, config?: Record<string, unknown>) => getApiInstance().delete(url, config),
};

export default api;
